import {
    ADMIN_SEARCHABLE_ATTRIBUTES,
    ALGOLIA_INDEX,
    DATA_SOURCES,
    DEFAULT_SEARCHABLE_ATTRIBUTES,
    SINGULAR_NAMES,
    isCovered,
} from '@/config/constants';
import {
    Configure,
    Index,
    InstantSearch,
    connectSearchBox,
    connectStateResults,
} from 'react-instantsearch-dom';
import { FiChevronDown, FiPlusCircle, FiSearch } from 'react-icons/fi';
import { createHashtagHits, filterAndFormatDuplicateHits } from 'utils/search';
import { hierarchyHintObjToStr, isSpateUser } from 'utils/helpers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@headlessui/react';
import { createProxySearchClient } from 'utils/api/algoliaSearchProxy';
import styled from 'styled-components';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';
import { usePlan } from 'utils/hooks';
import { useSelector } from 'react-redux';

import DimensionIcon from '../../DimensionIcon';
import IconButton from '../../Button/IconButton';
import SemanticList from '../../SemanticList';
import Stack from '../../Stack';

const SearchPanel = styled(Stack)`
    overflow: auto;
    max-height: 600px;
    position: absolute;
    z-index: 1;
    margin-top: 2px;
    padding: 1.5rem;
    width: 34.8rem;
    background-color: ${({ theme }) => theme.gray_7};
    border: 1px solid ${({ theme }) => theme.gray_5};
    box-shadow:
        0.260636px 6.99515px 11px rgba(0, 0, 0, 0.08),
        0.153463px 4.11874px 5.7376px rgba(0, 0, 0, 0.057856),
        0.0316934px 0.85061px 1.1792px rgba(0, 0, 0, 0.035072);
    border-radius: 4px;
    ${({ $align }) => ($align === 'right' ? 'right: 0;' : 'left: 0;')}
`;

const HitsWrapper = styled(SemanticList)`
    display: flex;
    flex-wrap: wrap;
    && {
        margin-bottom: -0.625rem;
    }
    & > * {
        margin: 0 0.625rem 0.625rem 0;
    }
`;

const ResultTypeHeadingWrapper = styled(Stack)`
    display: flex;
    align-items: center;
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ theme }) => theme.gray_1};
    text-transform: capitalize;
`;

const ResultTypeHeadingIcon = styled(DimensionIcon)`
    color: ${({ theme }) => theme.gray_3};
`;

const typeHeading = Object.entries(SINGULAR_NAMES).reduce(
    (acc, [plural, singular]) => {
        acc[singular] = singular === 'affinity' ? 'related searches' : plural;
        return acc;
    },
    {},
);

function ResultTypeHeading({ type }) {
    return (
        <ResultTypeHeadingWrapper $direction="row" $space=".375rem">
            <ResultTypeHeadingIcon dimensionType={type} />
            <span>{typeHeading[type]}</span>
        </ResultTypeHeadingWrapper>
    );
}

const SearchInputWrapper = styled(Stack)`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const SearchInputLabel = styled.label`
    font-family: 'Montserrat';
    font-weight: ${({ theme }) => theme.fontBold};
    font-size: ${({ theme }) => theme.fontSize_base};
    line-height: ${({ theme }) => theme.fontSize_md};
    color: ${({ theme }) => theme.gray_2};
    display: block;
`;

const FilterSearch = styled.input`
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    border: solid 1px ${({ theme }) => theme.gray_4};
    border-radius: ${({ theme }) => theme.borderRadius_md};
    color: inherit;
    padding: 0.5rem 0.75rem;
    padding-left: 2.25rem;
    flex-grow: 1;
`;

const IconWrapper = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    height: 100%;
    left: 0.5rem;
`;

const SearchIcon = styled(FiSearch)`
    position: absolute;
    color: ${({ theme }) => theme.purple_1};
    font-size: 1.25em;
`;

const SearchInputAndIconWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
`;

const defaultSearchInputLabelText = 'Search and add to watchlist';

function SearchInput({
    value,
    onChange,
    searchInputLabelText = defaultSearchInputLabelText,
}) {
    const inputRef = useRef();
    return (
        <SearchInputWrapper $direction="column" $space="10px">
            <SearchInputLabel htmlFor="add-by-search">
                {searchInputLabelText}
            </SearchInputLabel>
            <SearchInputAndIconWrapper>
                <FilterSearch
                    id="add-by-search"
                    type="text"
                    autoFocus
                    value={value}
                    onChange={onChange}
                    ref={inputRef}
                />
                <IconWrapper onClick={(e) => inputRef.current.focus()}>
                    <SearchIcon />
                </IconWrapper>
            </SearchInputAndIconWrapper>
        </SearchInputWrapper>
    );
}

function SearchResults({ searchResults, renderHits, combinedHits, queryID }) {
    const getHierarchy = useGetHierarchy();
    if (!(searchResults && searchResults.hits)) return null;
    if (!searchResults.hits.length && searchResults.query)
        return <div css="text-align: center;">No results available</div>;

    let groupedHits = filterAndFormatDuplicateHits(
        searchResults.hits,
        getHierarchy,
    );
    const hashtagHits = createHashtagHits(combinedHits);
    if (hashtagHits.length) {
        groupedHits = [...groupedHits, hashtagHits];
    }

    return (
        <>
            {groupedHits.map(([type, hits]) => (
                <Stack $space=".85rem" $direction="column" key={type}>
                    <ResultTypeHeading type={type} />
                    <HitsWrapper>
                        {hits.map((hit, idx) =>
                            renderHits(hit, queryID, idx + 1),
                        )}
                    </HitsWrapper>
                </Stack>
            ))}
        </>
    );
}

function SearchResultsSkeleton() {
    return (
        <Stack $space=".85rem" $direction="column">
            <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" />
            <HitsWrapper>
                {[80, 112, 64, 96, 72].map((w, i) => (
                    <div
                        key={i}
                        className="animate-pulse rounded bg-gray-200"
                        style={{ width: w, height: 34 }}
                    />
                ))}
            </HitsWrapper>
            <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
            <HitsWrapper>
                {[96, 72, 112].map((w, i) => (
                    <div
                        key={i}
                        className="animate-pulse rounded bg-gray-200"
                        style={{ width: w, height: 34 }}
                    />
                ))}
            </HitsWrapper>
        </Stack>
    );
}

const AddBySearchWrapper = styled(Popover)`
    position: relative;
    display: inline-block;
`;

const extraSearchClient = createProxySearchClient();

const MAX_QUERY_LENGTH = 512;

function UnconnectedAddBySearch({
    renderHits,
    searchResults,
    searching,
    error,
    refine,
    panelAlign,
    secondaryFacetFilters,
}) {
    const [value, setValue] = useState(
        searchResults ? searchResults.query : '',
    );
    const [extraHits, setExtraHits] = useState([]);

    const queryID =
        searchResults && searchResults.queryID ? searchResults.queryID : null;
    const queryTooLong = value.length > MAX_QUERY_LENGTH;

    useEffect(() => {
        const runExtraQuery = async () => {
            if (!value || value.length > MAX_QUERY_LENGTH) return;
            try {
                const { results } = await extraSearchClient.search([
                    {
                        indexName: ALGOLIA_INDEX,
                        params: {
                            query: value,
                            hitsPerPage: 10,
                            facetFilters: secondaryFacetFilters,
                        },
                    },
                ]);
                setExtraHits(results?.[0]?.hits || []);
            } catch (error) {
                console.error('Failed to run extra search query:', error);
                setExtraHits([]);
            }
        };

        runExtraQuery();
    }, [value]);
    const combinedHits = (() => {
        const primaryHits = searchResults?.hits || [];
        const extraFiltered = extraHits.filter(
            (hit) =>
                !primaryHits.some(
                    (primary) => primary.objectID === hit.objectID,
                ),
        );
        return [...extraFiltered, ...primaryHits];
    })();
    const shouldShowSkeleton =
        searching && !(searchResults?.hits?.length || combinedHits?.length);
    return (
        <AddBySearchWrapper>
            <Popover.Button
                as={IconButton}
                IconLeft={FiPlusCircle}
                IconRight={FiChevronDown}
                intent="primary"
                size="default"
            >
                Add
            </Popover.Button>
            <Popover.Panel
                as={SearchPanel}
                $direction="column"
                $space="24px"
                $align={panelAlign}
            >
                <SearchInput
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        if (
                            e.target.value &&
                            e.target.value.length <= MAX_QUERY_LENGTH
                        )
                            refine(e.target.value);
                    }}
                />
                {queryTooLong ? (
                    <div className="text-center text-neutral-500">
                        Search query is too long (max {MAX_QUERY_LENGTH}{' '}
                        characters).
                    </div>
                ) : shouldShowSkeleton ? (
                    <SearchResultsSkeleton />
                ) : error ? (
                    <div className="text-center text-neutral-500">
                        {error.message ||
                            'Search is temporarily unavailable. Please try again.'}
                    </div>
                ) : (
                    <SearchResults
                        searchResults={searchResults}
                        renderHits={renderHits}
                        combinedHits={combinedHits}
                        queryID={queryID}
                    />
                )}
            </Popover.Panel>
        </AddBySearchWrapper>
    );
}

const connectComponent = (Component) =>
    [connectStateResults, connectSearchBox].reduce((c, fn) => fn(c), Component);

const ConnectedAddBySearch = connectComponent(UnconnectedAddBySearch);

const createSearchClient = () => createProxySearchClient();

function AddBySearch({
    countryTextId,
    SearchComponent = ConnectedAddBySearch,
    ...props
}) {
    // TODO: move to session reducer
    const analyticsTags = useSelector(({ session }) => [
        session &&
        isSpateUser({
            company: session.company,
            email: session.user_email,
        })
            ? 'spate'
            : 'notSpate',
    ]);

    const searchClient = useMemo(() => createSearchClient(), []);

    const facetFilters = [['type:brand', 'depth:cat5']];
    const secondaryFacetFilters = [['hasTikTokViews:1', 'hasInstagramLikes:1']];
    if (countryTextId) {
        facetFilters.push(`country:${countryTextId}`);
        secondaryFacetFilters[0].push(`country:${countryTextId}`);
    }
    const { industry: userIndustries } = usePlan();
    const industry =
        userIndustries && userIndustries.length
            ? userIndustries.filter(
                  (industryName) =>
                      !['manipulation', 'dogs', 'flavors'].includes(
                          industryName,
                      ),
              )
            : null;

    let industryFacets = null;
    if (industry) {
        industryFacets = industry;
    }

    if (industryFacets)
        facetFilters.push(industryFacets.map((cat1) => `industry:${cat1}`));
    if (industryFacets) {
        const relatedSearchCat1s = ['product specifics', 'body parts'];
        secondaryFacetFilters.push([
            'type:-category',
            ...industryFacets
                .concat(relatedSearchCat1s)
                .map((cat1) => `industry:${cat1}`),
        ]);
        relatedSearchCat1s.forEach((relatedSearchCat1) => {
            secondaryFacetFilters.push([
                'type:-category',
                `industry:-${relatedSearchCat1}`,
                'depth:cat5',
            ]);
        });
        ['brand', 'product'].forEach((type) => {
            secondaryFacetFilters.push([
                `type:-${type}`,
                ...industryFacets
                    .concat(relatedSearchCat1s)
                    .map((cat1) => `industry:${cat1}`),
            ]);
        });
    }

    return (
        <InstantSearch indexName={ALGOLIA_INDEX} searchClient={searchClient}>
            <Index indexName={ALGOLIA_INDEX}>
                <Configure
                    hitsPerPage={10}
                    analyticsTags={analyticsTags}
                    facetFilters={facetFilters}
                    restrictSearchableAttributes={DEFAULT_SEARCHABLE_ATTRIBUTES}
                    clickAnalytics
                />
            </Index>
            <SearchComponent
                secondaryFacetFilters={secondaryFacetFilters}
                {...props}
            />
        </InstantSearch>
    );
}

export default AddBySearch;
